import { Component, computed, inject, input, linkedSignal, output, untracked } from '@angular/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SKELETON, LoaderComponent } from '@progress/kendo-angular-indicators';
import { KENDO_CARD, KENDO_LAYOUT } from '@progress/kendo-angular-layout';
import { TransitionMiniDto, UnityWorkflowState } from '@unity/clients';
import { IconService } from '@unity/services/icon.service';
import { ChipAuditPopupSharedComponent } from '@unity/shared/chip-audit-popup-shared/chip-audit-popup-shared.component';
import { ChipWorkflowStateSharedComponent } from '@unity/shared/presentation/chip-workflow-state-shared/chip-workflow-state-shared.component';
import { DetailedDto } from '@unity/utils/types';
import { SurveyModule } from 'survey-angular-ui';
import { SurveyModel } from 'survey-core';
import { StepperSurveySharedComponent } from '../stepper-survey-shared/stepper-survey-shared.component';

@Component({
  selector: 'unity-card-manage-survey-shared',
  imports: [
    KENDO_LAYOUT,
    KENDO_SKELETON,
    KENDO_BUTTON,
    KENDO_CARD,
    SurveyModule,
    StepperSurveySharedComponent,
    ChipWorkflowStateSharedComponent,
    LoaderComponent,
    ChipAuditPopupSharedComponent,
  ],
  templateUrl: './card-manage-survey-shared.component.html',
  styleUrl: './card-manage-survey-shared.component.scss',
})
export class CardManageSurveySharedComponent {
  private readonly iconService = inject(IconService);

  protected saveIcon = this.iconService.saveIcon;
  protected editIcon = this.iconService.pencilIcon;
  protected cancelIcon = this.iconService.cancelIcon;

  /* Inputs */
  public readonly $surveyModel = input.required<SurveyModel>({ alias: 'surveyModel' });
  public readonly $transitions = input.required<TransitionMiniDto[]>({ alias: 'transitions' });
  public readonly $isEditable = input.required<boolean | undefined>({ alias: 'isEditable' });
  public readonly $isEditing = input.required<boolean>({ alias: 'isEditing' });
  public readonly $isSaving = input.required<boolean>({ alias: 'isSaving' });
  public readonly $resource = input.required<DetailedDto | undefined>({ alias: 'resource' });
  public readonly $headerMeta = input<string>('Details', { alias: 'headerMeta' });
  public readonly $workflowCanChange = input(true, { alias: 'workflowCanChange' });

  public readonly editEvent = output();
  public readonly cancelEvent = output();
  public readonly saveEvent = output<TransitionMiniDto | undefined>();

  protected readonly $showSubmit = linkedSignal(() => {
    const surveyModel = this.$surveyModel();
    return surveyModel?.PageCount === 1;
  });

  protected readonly $currentStatus = computed(() => {
    const resource = this.$resource();
    return resource ? resource.current_state : { id: 0, enum_value: UnityWorkflowState.Unknown, name: 'Unknown' };
  });

  protected handleEdit() {
    this.editEvent.emit();
  }

  protected handleCancel() {
    this.cancelEvent.emit();
  }

  protected handleSave(transition?: TransitionMiniDto) {
    this.saveEvent.emit(transition);
  }

  protected onPageChangeEvent(nextPage: number) {
    const surveyModel = untracked(this.$surveyModel);
    const showSubmit = untracked(this.$showSubmit);
    if (!surveyModel) {
      return;
    }
    const isLastPage = surveyModel.PageCount === nextPage;
    if (isLastPage && !showSubmit) {
      this.$showSubmit.set(true);
      return;
    }
    if (!isLastPage && showSubmit) {
      this.$showSubmit.set(false);
    }
  }
}
